import React from 'react';
import { useTranslation } from 'react-i18next';
import { Group, Stack, Text } from '@mantine/core';
import { DataTable, DataTableProps, useDataTableColumns } from 'mantine-datatable';

import { COLORS } from 'constants/colors';
import { NOT_APPLICABLE } from 'constants/defaultValues';
import { dispatchStatusDisplayNames } from 'constants/dispatch';

import { isNumber } from 'util/numUtils';
import { getDisplayForIsEMIADCP } from 'util/samplingHistory';
import { userFullName } from 'util/userRoles';
import { DispatchType } from 'store/dispatch/types';

import { DispatchActions } from '../Actions/DispatchActions';

import { DispatchExpandedRow } from './DispatchExpandedRow';

type DispatchTablePropsType = Required<
  Pick<
    DataTableProps<DispatchType>,
    'fetching' | 'records' | 'totalRecords' | 'recordsPerPage' | 'page' | 'onPageChange'
  >
> & {
  refreshPage: VoidFunction;
};

const STORE_COLUMNS_KEY = 'dispatch-plans-columns';

export const DispatchTable = ({
  fetching,
  records,
  totalRecords,
  recordsPerPage,
  page,
  onPageChange,
  refreshPage,
}: DispatchTablePropsType) => {
  const { t } = useTranslation();

  const columns = [
    {
      accessor: 'agency_operation_field',
      title: (
        <Stack gap="0" style={{ color: 'var(--mantine-color-text)' }}>
          <Text fw={600} size="xs">{`${t('agency')}`}</Text>
          <Group gap="0.2rem" wrap="wrap">
            <Text fw="inherit">{`${t('operation').substring(0, 2)}:`}</Text>
            <Text fw="inherit">{`${t('field')}`}</Text>
          </Group>
        </Stack>
      ),
      width: 250,
      minWidth: 200,
      resizable: true,
      render: (row: DispatchType) => (
        <Stack gap="0" style={{ color: 'var(--mantine-color-text)' }}>
          <Text size="xs">{row.agency?.name}</Text>
          <Group gap="0.2rem" wrap="wrap">
            <Text>{`${row.operation_name}:`}</Text>
            <Text>{`${row.field_name}`}</Text>
          </Group>
        </Stack>
      ),
    },
    {
      accessor: 'primary_sku_name',
      title: t('order'),
      width: 120,
      minWidth: 80,
      resizable: true,
      render: (row: DispatchType) => (
        <Text style={{ whiteSpace: 'nowrap' }}>{row.primary_sku_name}</Text>
      ),
    },
    {
      accessor: 'dispatch_status',
      title: t('status'),
      width: 120,
      minWidth: 80,
      resizable: true,
      render: (row: DispatchType) => (
        <Stack gap="0">
          <Text>{t(dispatchStatusDisplayNames[row.dispatch_status])}</Text>
          <Text size="xs">{`${t('sample')}: ${0}/${12}`}</Text>
        </Stack>
      ),
    },
    {
      accessor: 'days_marked_ready',
      title: t('daysReady'),
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      resizable: true,
      render: (row: DispatchType) => (
        <Text>
          {isNumber(row.days_marked_ready) ? String(row.days_marked_ready) : NOT_APPLICABLE}
        </Text>
      ),
    },
    {
      accessor: 'field_activity',
      title: t('fieldActivity'),
      width: 250,
      minWidth: 150,
      resizable: true,
      render: (row: DispatchType) => (
        <Stack gap="0">
          <Text size="xs">{`${t('sampler')}: ${row.assigned_to_id ? userFullName(row.assigned_to) : NOT_APPLICABLE}`}</Text>
          <Text size="xs">{`${t('scan')}: ${row.assigned_to_scan_id ? userFullName(row.assigned_to_scan) : NOT_APPLICABLE}`}</Text>
          <Text
            size="xs"
            c="gray.6"
          >{`ADCP: ${t(getDisplayForIsEMIADCP(Boolean(row.adcp_confirmed_at)))}`}</Text>
        </Stack>
      ),
    },
    {
      accessor: 'dispatch_notes',
      title: t('dispatchNotes'),
      grow: true,
      resizable: true,
      render: (row: DispatchType) => <Text size="sm">{row.dispatch_notes || t('none')}</Text>,
    },
    {
      accessor: 'notes',
      title: t('samplerNotes'),
      grow: true,
      resizable: true,
      render: (row: DispatchType) => <Text size="sm">{row.notes || t('none')}</Text>,
    },
    {
      accessor: 'actions',
      title: '',
      width: 100,
      minWidth: 100,
      maxWidth: 100,
      render: (row: DispatchType) => <DispatchActions dispatchPlan={row} onSubmit={refreshPage} />,
    },
  ];

  const { effectiveColumns } = useDataTableColumns({ key: STORE_COLUMNS_KEY, columns });

  return (
    <DataTable
      minHeight={200}
      fetching={fetching}
      columns={effectiveColumns}
      records={records}
      totalRecords={totalRecords}
      recordsPerPage={recordsPerPage}
      page={page}
      onPageChange={onPageChange}
      storeColumnsKey={STORE_COLUMNS_KEY}
      style={{ color: COLORS.black }}
      rowExpansion={{
        allowMultiple: true,
        content: ({ record }) => <DispatchExpandedRow dispatchPlan={record} />,
      }}
    />
  );
};
